import { DataClassification, Policy, SEnumTypeMapping, ValidationMessage } from '@summit/core';
import {
  CellClassParams,
  ICellRendererParams,
  IRowNode,
  ValueFormatterParams,
  ValueGetterParams
} from 'ag-grid-enterprise';

import { AppConfig } from '../../services';
import { ObjectSchema } from '../display-schema';
import { DisplayType } from '../display-type.enum';
import { EquationTypes } from '../equation-types.enum';
import { SummitAction } from '../summit-action';
import { SummitIdObject } from '../summit-id-object';

// ViewField is the description for a field in the schema.
// It is used as Attributes in the DisplaySchema class.
// TBD View Attribute
export class ViewField<T> extends SummitIdObject {
  override id: string;
  key?: keyof T & string;
  propertyName: keyof T & string;
  excelSubProperty?: keyof T & string; //used when excel needs a different property
  formField?: boolean;
  columnField?: boolean; // Display in List form - only used at root tree level
  // How to display
  displayName?: string;
  displayNameSuffixIcon?: string;
  displayHint?: string;
  displayHints?: { [key: string]: string };
  displayClass?: string;
  displayType?: DisplayType;
  //String for Properties, Record for Enums
  displayTypeParameter?: string | SEnumTypeMapping | boolean;
  displayNullText?: string;
  dataClassification?: DataClassification;
  template?: boolean; // Display Template  //phase out after grid move
  renderer?: string | unknown; // Used for custom renderers
  icon?: string;
  width?: number;
  widthMin?: number;
  widthMax?: number;
  widthFlex?: number;
  //Grid Classes
  headerClass?: string[];
  // Used to override the default valueFormatter
  // Can be used to not show values or apply a Null string
  // Partial is used here to allow Summit calls to pass in a subset of the params
  valueGetterOverride?: (params: Partial<ValueGetterParams>) => unknown;
  // Used to override the default valueFormatter
  // Does not work where you want to return Null string, Null string tells grid to just use value.
  valueFormatterOverride?: (params: ValueFormatterParams) => unknown;
  showPing?: (params: ICellRendererParams<T>) => boolean; // Show ping icon in the cell
  cellClassRules?: {
    [cssClassName: string]: ((params: CellClassParams<T>) => boolean) | string;
  };

  // Validation message
  validationMessages?: ValidationMessage[]; // Form Validation rules and Messages
  // Policy
  policyRestriction?: Policy = Policy.Any; // Limit who can see it;
  children?: ObjectSchema<T>;

  // Sort Order
  sortParameter?: keyof T & string;
  sortNullFirst?: boolean; // defaults to False/No
  comparator?: (
    valueA: T[keyof T],
    valueB: T[keyof T],
    nodeA: IRowNode<T>,
    nodeB: IRowNode<T>,
    isDescending: boolean
  ) => number;

  // Footer Fields
  footerEquation?: EquationTypes;
  footerFilter?: (arg0: T) => boolean; // Used to filter the footer values
  footerEquationValues?: (keyof T & string)[];
  templateFooter?: boolean; //Phasing out after grid move

  // link Values
  action?: SummitAction;
  linkPolicyRestriction?: Policy = Policy.Any; // Limit the lowest permissions level;
  routerLink?: string[];
  queryParams?: string[][];

  constructor(jsonData?: Partial<ViewField<T>>, key?: keyof T & string) {
    super(jsonData);
    if (jsonData) {
      if (jsonData.validationMessages) {
        this.validationMessages = jsonData.validationMessages.map((data) => {
          return new ValidationMessage(data);
        });
      }
      if (jsonData.children) {
        this.children = jsonData.children;
      }
      if (jsonData.footerEquationValues) {
        this.footerEquationValues = jsonData.footerEquationValues.map((data) => data);
      }
      if (jsonData.queryParams) {
        this.queryParams = jsonData.queryParams.sort((n1, n2) => {
          if (n1[0] > n2[0]) {
            return 1;
          }
          if (n1[0] < n2[0]) {
            return -1;
          }
          return 0;
        });
      }
    }
    if (key) {
      this.key = key;
    }
  }

  static UseCustomRenderer<T extends SummitIdObject>(attribute: ViewField<T>): boolean {
    return attribute.icon ||
      attribute.routerLink ||
      attribute.action ||
      attribute.displayType === DisplayType.FormattedText ||
      attribute.displayType === DisplayType.ShortStringArrayFT ||
      attribute.displayType === DisplayType.ShortStringArrayWithParameterFT ||
      attribute.displayType === DisplayType.HTML ||
      (attribute.footerEquation && attribute.footerEquation !== EquationTypes.Sum) ||
      attribute.showPing != undefined ||
      attribute.renderer
      ? true
      : false;
  }

  static IsBlurred<T extends SummitIdObject>(attribute: ViewField<T>, layout: Partial<AppConfig>): boolean {
    if (layout.blurFinancial && !attribute.dataClassification) {
      if (
        attribute.displayType === DisplayType.Money ||
        attribute.displayType === DisplayType.Rate ||
        attribute.displayType === DisplayType.Financial
      ) {
        return true;
      }
    }
    return (
      (attribute.dataClassification === 'Pii' && layout.blurPii === true) ||
      (attribute.dataClassification === 'Financial' && layout.blurFinancial === true)
    );
  }
}
